/**
 * Singleton timer manager for reconnection popup
 * Ensures only one timer can exist across the entire application
 */
class ReconnectionTimerManager {
  private static instance: ReconnectionTimerManager;
  private timer: NodeJS.Timeout | null = null;
  private isActive = false;
  private currentInstanceId: string | null = null;
  private onTick: ((countdown: number, elapsedTime: number) => void) | null = null;
  private onRetry: (() => void) | null = null;
  private retryCount = 0;
  private currentCountdown = 1;
  private currentAttemptDelay = 1;
  private startTime = 0;
  private readonly MAX_RETRIES = 5;
  private readonly TOTAL_RETRY_TIME = 65; // 3 + 5 + 12 + 15 + 30 = 65 seconds

  private constructor() {}

  public static getInstance(): ReconnectionTimerManager {
    if (!ReconnectionTimerManager.instance) {
      ReconnectionTimerManager.instance = new ReconnectionTimerManager();
    }
    return ReconnectionTimerManager.instance;
  }

  /**
   * Start the reconnection timer
   * @param instanceId - Unique identifier for the popup instance
   * @param onTick - Callback for countdown ticks
   * @param onRetry - Callback for retry attempts
   */
  public start(instanceId: string, onTick: (countdown: number, elapsedTime: number) => void, onRetry: () => void): void {
    // If already active, stop the previous timer
    if (this.isActive) {
      this.stop();
    }

    this.currentInstanceId = instanceId;
    this.onTick = onTick;
    this.onRetry = onRetry;
    this.retryCount = 0;
    this.currentCountdown = 1;
    this.currentAttemptDelay = 1;
    this.startTime = Date.now();
    this.isActive = true;

    // Start the timer
    this.timer = setInterval(() => {
      if (!this.isActive || !this.onTick) {
        this.stop();
        return;
      }

      const elapsedTime = Math.floor((Date.now() - this.startTime) / 1000);
      this.onTick(this.currentCountdown, elapsedTime);

      if (this.currentCountdown <= 1) {
        if (this.retryCount < this.MAX_RETRIES) {
          this.retryCount++;
          if (this.onRetry) {
            this.onRetry();
          }
          
          // Set countdown for next retry attempt
          this.currentAttemptDelay = this.getRetryDelay(this.retryCount);
          this.currentCountdown = this.currentAttemptDelay;
        } else {
          // Max retries reached, but continue tracking elapsed time
          this.currentCountdown = 1; // Keep countdown at 1 to continue ticking
          this.currentAttemptDelay = 1; // Set delay to 1 for continuous updates
        }
      } else {
        this.currentCountdown--;
      }
    }, 1000);
  }

  /**
   * Stop the reconnection timer
   */
  public stop(): void {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
    
    this.isActive = false;
    this.currentInstanceId = null;
    this.onTick = null;
    this.onRetry = null;
    this.retryCount = 0;
    this.currentCountdown = 1;
    this.currentAttemptDelay = 1;
    this.startTime = 0;
  }

  /**
   * Reset the timer manager to initial state
   * This should be called when connection is fully restored
   */
  public reset(): void {
    this.stop();
    // Additional reset logic can be added here if needed
  }

  /**
   * Check if timer is active
   */
  public isTimerActive(): boolean {
    return this.isActive;
  }

  /**
   * Get current retry count
   */
  public getRetryCount(): number {
    return this.retryCount;
  }

  /**
   * Get current countdown
   */
  public getCurrentCountdown(): number {
    return this.currentCountdown;
  }

  /**
   * Get the delay for the next retry attempt
   */
  private getRetryDelay(retryCount: number): number {
    switch (retryCount) {
      case 0: return 3;   // First retry: 3 seconds
      case 1: return 5;   // Second retry: 5 seconds
      case 2: return 12;  // Third retry: 12 seconds
      case 3: return 15;  // Fourth retry: 15 seconds
      case 4: return 30;  // Fifth retry: 30 seconds
      default: return 30; // Default: 30 seconds
    }
  }
}

export const reconnectionTimerManager = ReconnectionTimerManager.getInstance();
